import { useHistory } from "react-router-dom";
import DetailRenderer from "../components/global/forms/DetailRenderer";
import Button from "../components/organism/Button";
import ErrorBoundary from "../components/global/ErrorBoundary";
import useProfile from "../services/utils/custom hooks/useProfile";
import AdminContentLayout from "../layouts/AdminContentLayout";
import { useEffect, useState } from "react";
import AssociatedOrganizationAndRolesTable from "../components/Tables/AssociatedOrganizationAndRoles";
import utils from "../services/utils";
import UserDetailLoadingIndicator from "../components/global/LoadingIndicators/UserDetailLoadingIndicator";
import CustomText from "../components/atomic/CustomText";
import useSignature from "../services/utils/custom hooks/eSignature/useSignature";
import SignatureViewer from "../components/global/SignatureViewer";
import CustomPopUp from "../components/global/CustomPopup";
import CustomSignatureInput from "../components/global/CustomSignatureInput";
import useUpdateEsignature from "../services/utils/custom hooks/eSignature/useUpdateEsignature";
import useCreateSignature from "../services/utils/custom hooks/eSignature/useCreateSignature";
import useDeleteSignature from "../services/utils/custom hooks/eSignature/useDeleteSignature";
import { IS_PERSONAL_NOTIFICATION_SETTINGS_ENABLED } from "../constants/notificationSettingsFlags";

function Profile(): JSX.Element {
  const history = useHistory();
  const { data, isLoading, isError, isFetching } = useProfile();
  const { data: signatureData, isLoading: fetchingSignatureData } =
    useSignature();

  const [showSignaturePopup, setShowSignaturePopup] = useState<boolean>(false);
  const { mutate, isPending, isSuccess } = useUpdateEsignature();
  const {
    mutate: createSignature,
    isPending: creatingSignature,
    isSuccess: createdSuccessfully,
  } = useCreateSignature();

  // const [detail, setUserDetail] = useState({
  //   logo: data?.user?.imageUrl,
  //   fullName: `${data?.user?.firstName}  ${data?.user?.lastName}`,
  //   email: data?.user?.email,
  //   role: data?.user?.roles.join(", "),
  // });
  const detail = {
    logo: data?.user?.imageUrl || data?.user?.profileImage,
    fullName: `${data?.user?.firstName}  ${data?.user?.lastName}`,
    email: data?.user?.email,
    role: data?.user?.roles?.join(", "),
    ...(data?.user?.roles?.includes("Physician") && {
      suffix: data.user.suffix,
      specialty: data.user.specialty,
      npi: data.user.npi,
    }),
  };
  const {
    mutate: deleteSignature,
    isPending: deletingSignature,
    isSuccess: deletedSuccessfully,
  } = useDeleteSignature({
    closePopup: () => {
      setShowDeletePopup(false);
    },
  });
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const isPhysician = data?.user?.roles?.includes?.("Physician");
  // const [userNotificationMediumTableBody, setUserNotificationMediumTableBody] =
  //   useState<Array<BodyColumnData[]>>([]);
  // const [
  //   userNotificationMediumTableColumn,
  //   setUserNotificationMediumTableColumn,
  // ] = useState<HeaderColumnData[]>([]);

  useEffect(() => {
    setShowSignaturePopup(false);
  }, [isSuccess, createdSuccessfully]);

  function handleSubmission(signatureUrl: string) {
    if (!!signatureData?.id)
      mutate({
        userId: data?.user?.id,
        base64URL: signatureUrl,
        fileName: `${data?.user?.id}_${data?.user?.firstName}_signature_file.png`,
      });
    else
      createSignature({
        userId: data?.user?.id,
        base64URL: signatureUrl,
        fileName: `${data?.user?.id}_${data?.user?.firstName}_signature_file.png`,
      });
  }

  // useEffect(() => {
  //   if (!isLoading) {
  //     if (data.user.roles.includes("Physician")) {
  //       setUserDetail((prevState) => ({
  //         ...prevState,
  //         suffix: data.user.suffix,
  //         specialty: data.user.specialty,
  //         npi: data.user.npi,
  //       }));
  //     }
  //   }
  // }, [isLoading]);
  return (
    <AdminContentLayout
      pageTitle="Profile | CSTL"
      subHeaderTitle="My Profile"
      rightSectionClassName="xs:overflow-x-hidden md:overflow-x-visible"
      customButtons={[
        {
          buttonTheme: "primary",
          textInformation: {
            text: "Edit",
            fontWeight: "font-bold",
          },
          className: "hidden md:block md:mx-5",
          onClick: () => {
            history.push("/access/profile/editprofile");
          },
        },
      ]}
    >
      <ErrorBoundary>
        {isLoading || isFetching || fetchingSignatureData ? (
          <UserDetailLoadingIndicator />
        ) : (
          <div className=" flex flex-col xs:items-stretch md:items-start overflow-auto">
            <div className="w-full overflow-y-auto overflow-x-hidden">
              <div className="relative w-full xs:px-3 md:px-5 ">
                <Button
                  buttonTheme="primary"
                  textInformation={{
                    text: "Edit",
                    fontWeight: "font-bold",
                  }}
                  className="self-end absolute right-3 top-5 md:hidden"
                  onClick={() => {
                    history.push("/access/profile/editprofile");
                  }}
                />
                <DetailRenderer
                  data={detail}
                  groupFields={
                    isPhysician
                      ? [
                          "logo",
                          "fullName",
                          "email",
                          "role",
                          "npi",
                          "suffix",
                          "specialty",
                        ]
                      : ["logo", "fullName", "email", "role"]
                  }
                  hiddenFields={[]}
                  roundedImage
                  showLoadingIndication={isLoading}
                />
              </div>
              <div className="flex md:items-center gap-x-6 xs:my-5 xs:ml-3 md:-mt-[30px] md:ml-[155px] mb-4 relative">
                <Button
                  buttonTheme="transparent"
                  textInformation={{
                    text: "Change Password",
                    fontWeight: "font-semibold",
                  }}
                  className="text-primary xs:text-left"
                  onClick={() => {
                    history.replace("/forget-password");
                  }}
                />
                {IS_PERSONAL_NOTIFICATION_SETTINGS_ENABLED && isPhysician && (
                  <div className="h-8 border-r border-gray-300" />
                )}
                {IS_PERSONAL_NOTIFICATION_SETTINGS_ENABLED && isPhysician && (
                  <Button
                    buttonTheme="transparent"
                    textInformation={{
                      text: "Configure Personal Notifications & Reports Delivery",
                      fontWeight: "font-semibold",
                    }}
                    className="text-primary xs:text-left"
                    onClick={() => {
                      history.replace(
                        "/access/notifications/settings/personal-settings"
                      );
                    }}
                  />
                )}
              </div>

              {isPhysician && (
                <div className="w-full border-t border-b border-roundedInputBorder py-6 px-5">
                  <CustomText text={"E-Signature"} fontWeight="font-semibold" />
                  <CustomText
                    text={"E-signature is required for order authorization."}
                    fontWeight="font-normal"
                    className="my-3.5"
                  />
                  <div className="mb-3">
                    <SignatureViewer
                      imageURL={signatureData?.viewableURL ?? ""}
                      alt="Signature"
                      containerClassName="w-[300px]"
                      maxHeight="250px"
                      maxWidth="300px"
                    />
                  </div>
                  <div className="flex justify-start items-center">
                    <Button
                      textInformation={{
                        text: signatureData?.id
                          ? "Change Signature"
                          : "Create Signature",
                        fontWeight: "font-semibold",
                      }}
                      buttonTheme="transparent"
                      className="text-primary"
                      onClick={() => {
                        setShowSignaturePopup(true);
                      }}
                    />
                    {!!signatureData?.id && (
                      <Button
                        textInformation={{
                          text: "Delete Signature",
                          fontWeight: "font-semibold",
                        }}
                        disabled={creatingSignature || fetchingSignatureData}
                        buttonTheme="transparent"
                        className="text-requiredRed ml-3"
                        onClick={() => {
                          setShowDeletePopup(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              {showDeletePopup && (
                <CustomPopUp
                  popUpTitle="E-Signature"
                  hidePopUp={() => setShowDeletePopup(false)}
                >
                  <CustomText
                    text={"Are you sure you want to delete the E-signature ?"}
                    fontWeight="font-normal"
                    className="text-requiredRed"
                  />
                  <div className="flex justify-end items-center mt-4">
                    <Button
                      textInformation={{
                        text: "No",
                        fontWeight: "font-semibold",
                      }}
                      buttonTheme="secondary"
                      disabled={deletingSignature}
                      onClick={() => {
                        setShowDeletePopup(false);
                      }}
                    />

                    <Button
                      textInformation={{
                        text: "Yes",
                        fontWeight: "font-semibold",
                      }}
                      disabled={deletingSignature}
                      loading={deletingSignature}
                      buttonTheme="primary"
                      className="ml-3"
                      onClick={() => {
                        deleteSignature();
                      }}
                    />
                  </div>
                </CustomPopUp>
              )}
              {showSignaturePopup && (
                <CustomPopUp
                  popUpTitle="E-Signature"
                  popUpSubtitle="E-signature is required for order authorization"
                  hidePopUp={() => setShowSignaturePopup(false)}
                >
                  <CustomSignatureInput
                    submitting={isPending || creatingSignature}
                    handleSubmission={handleSubmission}
                  />
                </CustomPopUp>
              )}
              <AssociatedOrganizationAndRolesTable
                tableContainerClass="xs:overflow-x-auto xs:w-[90vw] xs:border-r xs:border-t"
                userRoles={data?.user?.roles}
                data={
                  data?.organizationsRolesAndSOF
                    ? data.organizationsRolesAndSOF.map((org: any) => ({
                        ...org,
                        roles: utils.physicianToClinician(org.roles).join(","),
                      }))
                    : []
                }
                isLoading={isLoading}
              />
            </div>
          </div>
        )}
      </ErrorBoundary>
    </AdminContentLayout>
  );
}

export default Profile;
